// StreamAnalyticsChart.jsx  – stats-only (no earnings)
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { getDoc, doc } from 'firebase/firestore';
import { IoFilter } from 'react-icons/io5';
import { database, firestore } from './firebase';
import { AppContext } from './AppContext';

/* ───── layout ───── */
const FRAME_WIDTH = 480;
const WIDTH = Math.min(window.innerWidth, FRAME_WIDTH);
const CHART_H = 220;
const SVG_W = WIDTH - 20;
const PAD = 20;
const EFF_W = SVG_W - 2 * PAD;
const EFF_H = CHART_H - 2 * PAD;

/* ───── date helpers ───── */
const fmtDate = d =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

const pastDays = n =>
  [...Array(n)].map((_, i) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - (n - 1 - i));
    return fmtDate(d);
  });

const isoWeek = ds => {
  const t = new Date(ds);
  const n = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - n + 3);
  const th = t.valueOf();
  t.setUTCMonth(0, 1);
  if (t.getUTCDay() !== 4) t.setUTCMonth(0, 1 + ((4 - t.getUTCDay()) + 7) % 7);
  return 1 + Math.ceil((th - t) / 604800000);
};

const pastWeeks = n =>
  [...Array(n)].map((_, i) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - (n - 1 - i) * 7);
    return `${d.getUTCFullYear()}-W${String(isoWeek(d)).padStart(2, '0')}`;
  });

const pastMonths = n =>
  [...Array(n)].map((_, i) => {
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() - (n - 1 - i), 1);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });

/* ───── helpers ───── */
const normalize = t =>
  t
    .toLowerCase()
    .replace(/\(.*?\)|\[.*?]|\s*-\s*.*$/g, '')
    .split(/\bft\.?|\bfeat\.?/)[0]
    .trim()
    .replace(/\s+/g, ' ');

const valOf = v =>
  typeof v === 'number' ? v : v && typeof v === 'object' ? Number(v.streams || 0) + Number(v.downloads || 0) : 0;

const sumMonth = (o, k) => Object.entries(o).reduce((s, [kk, v]) => (kk.startsWith(k) ? s + valOf(v) : s), 0);
const sumIsoWeek = (o, w) =>
  Object.entries(o).reduce((s, [kk, v]) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(kk)) return s;
    const ww = `${kk.slice(0, 4)}-W${String(isoWeek(kk)).padStart(2, '0')}`;
    return ww === w ? s + valOf(v) : s;
  }, 0);

const genPath = data => {
  if (!data.length) return '';
  const max = Math.max(...data);
  return data
    .map(
      (d, i) =>
        `${i === 0 ? 'M' : 'L'} ${PAD + (i / (data.length - 1)) * EFF_W} ${
          PAD + (EFF_H - (d / (max || 1)) * EFF_H)
        }`,
    )
    .join(' ');
};

/* ───────────────────────── component ───────────────────────── */
export default function StreamAnalyticsChart() {
  /* 1.  read navigation state */
  const { state } = useLocation();
  const assetId = state?.assetId;
  const songName = state?.songName;

  /* 2.  hooks that must run unconditionally */
  const { assetsSummary } = useContext(AppContext);
  const normTitle = useMemo(() => normalize(songName || ''), [songName]);

  const [platform, setPlatform] = useState('all'); // all | spotify | apple | amazon | spndat
  const [timeframe, setTimeframe] = useState('3m'); // week | month | 3m
  const [labels, setLabels] = useState([]);
  const [values, setValues] = useState([]);

  const [spndatStats, setSpndatStats] = useState(null);
  const [summary, setSummary] = useState(null);

  const [hoverIdx, setHoverIdx] = useState(null);
  const [hoverY, setHoverY] = useState(null);
  const [modal, setModal] = useState(false);

  /* 3.  guard fetches when assetId / songName are missing */
  // Realtime-DB (SPNDAT)
  useEffect(() => {
    if (!assetId) return;
    (async () => {
      const root = `streamStats/${assetId}`;
      const [d, w, m] = await Promise.all([
        get(ref(database, `${root}/daily`)),
        get(ref(database, `${root}/weekly`)),
        get(ref(database, `${root}/monthly`)),
      ]);
      setSpndatStats({
        daily: d.exists() ? d.val() : {},
        weekly: w.exists() ? w.val() : {},
        monthly: m.exists() ? m.val() : {},
      });
    })();
  }, [assetId]);

  // Firestore master summary
  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(firestore, 'analyticsSummaries', 'summary_1'));
      setSummary(snap.exists() ? snap.data() : {});
    })();
  }, []);

  /* 4.  resolver */
  const getVal = (d, plat, date) => {
    const meta = Array.isArray(assetsSummary) ? assetsSummary.find(a => a.id === assetId) : null;
    const trueKey = [assetId, meta?.contentId].filter(Boolean).find(k => d[k]);

    const bucket = /^\d{4}-\d{2}-\d{2}$/.test(date) ? 'daily' : date.includes('W') ? 'weekly' : 'monthly';

    if (plat === 'spndat') return Number(spndatStats?.[bucket]?.[date] || 0);

    if (!trueKey && plat !== 'all') return 0;

    const asset = trueKey ? d[trueKey] : {};
    const amazon = asset.amazon_music?.dates || {};
    const spotify = asset.spotify?.dates || {};
    const apple = asset.apple_music?.dates || {};

    const calc = (src, isApple = false) => {
      if (date.length === 7) return sumMonth(src, date);
      if (date.includes('W')) return sumIsoWeek(src, date);
      return isApple ? valOf(src[date]) : Number(src[date] || 0);
    };

    if (plat === 'amazon') return calc(amazon);
    if (plat === 'spotify') return calc(spotify);
    if (plat === 'apple') return calc(apple, true);

    const sp = Number(spndatStats?.[bucket]?.[date] || 0);
    return calc(amazon) + calc(spotify) + calc(apple, true) + sp;
  };

  /* 5.  build chart arrays */
  useEffect(() => {
    if (!summary || !assetId) return;

    let keys =
      timeframe === 'week' ? pastDays(7) : timeframe === 'month' ? pastWeeks(4) : pastMonths(3);

    // anchor to latest activity
    if (timeframe === 'week') {
      const last = [...pastDays(180)].reverse().find(d => getVal(summary, platform, d) > 0);
      if (last) {
        keys = [...Array(7)].map((_, i) => {
          const dt = new Date(last);
          dt.setUTCDate(dt.getUTCDate() - (6 - i));
          return fmtDate(dt);
        });
      }
    }
    if (timeframe === 'month') {
      const w12 = pastWeeks(12);
      const lw = [...w12].reverse().find(w => getVal(summary, platform, w) > 0);
      if (lw) {
        const idx = w12.indexOf(lw);
        keys = w12.slice(Math.max(0, idx - 3), idx + 1);
      }
    }

    setLabels(keys);
    setValues(keys.map(d => getVal(summary, platform, d)));
  }, [summary, spndatStats, platform, timeframe, assetId]);

  /* 6.  SVG helpers */
  const linePath = genPath(values);
  const fillPath = linePath
    ? `${linePath} L ${SVG_W - PAD} ${CHART_H - PAD} L ${PAD} ${CHART_H - PAD} Z`
    : '';
  const maxVal = values.length ? Math.max(...values) : 1;
  const xTicks = labels.length
    ? labels.length === 1
      ? [labels[0]]
      : [labels[0], labels[Math.floor(labels.length / 2)], labels[labels.length - 1]]
    : [];
  const fmtVal = v => (Number.isInteger(v) ? String(v) : v.toFixed(1));

  const ptrMove = (x, y, r) => {
    const rel = x - r.left - PAD;
    if (rel < 0 || rel > EFF_W) {
      setHoverIdx(null);
      return;
    }
    const idx = Math.round((rel / EFF_W) * (values.length - 1));
    setHoverIdx(Math.max(0, Math.min(idx, values.length - 1)));
    setHoverY(y - r.top);
  };

  /* 7.  render fallback if navigation missed data */
  if (!assetId || !songName) {
    return <div className="analytics-wrap"><div className="dc-card"><div className="dc-note">No asset selected.</div></div>{styles}</div>;
  }

  /* 8.  UI */
  return (
    <div className="analytics-wrap">
      <div className="dc-card" style={{ maxWidth: FRAME_WIDTH, margin: '0 auto' }}>
        {/* header */}
        <div className="ana-header">
          <div className="ana-title" title={normTitle}>
            {platform === 'all' ? 'All Platforms' : platform} • {timeframe === '3m' ? '3 Months' : timeframe}
          </div>
          <button className="dc-btn ghost small" onClick={() => setModal(true)}>
            <IoFilter size={16} style={{ marginRight: 6 }} />
            Filter
          </button>
        </div>

        {/* chart */}
        <div className="ana-chart">
          <svg
            width="100%"
            height={CHART_H}
            viewBox={`0 0 ${SVG_W} ${CHART_H}`}
            onMouseMove={e => ptrMove(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={() => {
              setHoverIdx(null);
              setHoverY(null);
            }}
            onTouchStart={e => ptrMove(e.touches[0].clientX, e.touches[0].clientY, e.currentTarget.getBoundingClientRect())}
            onTouchMove={e => ptrMove(e.touches[0].clientX, e.touches[0].clientY, e.currentTarget.getBoundingClientRect())}
            onTouchEnd={() => {
              setHoverIdx(null);
              setHoverY(null);
            }}
          >
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--brand-top)" stopOpacity="0.45" />
                <stop offset="100%" stopColor="var(--brand-bot)" stopOpacity="0" />
              </linearGradient>
            </defs>

            {fillPath && <path d={fillPath} fill="url(#areaGrad)" />}
            {linePath && <path d={linePath} stroke="var(--brand-top)" strokeWidth="2" fill="none" />}

            {values.map((v, i) => {
              const x = PAD + (i / (values.length - 1)) * EFF_W;
              const y = PAD + (EFF_H - (v / maxVal) * EFF_H);
              return (
                <g key={i}>
                  <circle cx={x} cy={y} r="3" fill="#fff" />
                  <text x={x} y={y - 10} fontSize="10" fontWeight="600" fill="#fff" textAnchor="middle">
                    {fmtVal(v)}
                  </text>
                </g>
              );
            })}

            {hoverIdx !== null &&
              (() => {
                const x = PAD + (hoverIdx / (values.length - 1)) * EFF_W;
                const anchor = x > SVG_W / 2 ? 'end' : 'start';
                const labelX = x + (anchor === 'end' ? -8 : 8);
                const labelY = Math.max(12, Math.min(CHART_H - 8, (hoverY ?? 0) - 10));
                return (
                  <>
                    <line x1={x} y1={0} x2={x} y2={CHART_H} stroke="#fff" strokeWidth="1" strokeDasharray="4" />
                    <text x={labelX} y={labelY} fontSize="12" fontWeight="600" fill="#fff" textAnchor={anchor}>
                      {labels[hoverIdx]}
                    </text>
                  </>
                );
              })()}
          </svg>
        </div>

        {/* x-axis ticks */}
        {xTicks.length > 0 && (
          <div className="ana-xaxis">{xTicks.map((t, i) => <span key={i} className="ana-x">{t}</span>)}</div>
        )}
      </div>

      {/* modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Select Options</h3>

            <OptRow title="Platform" list={['all', 'spotify', 'apple', 'amazon', 'spndat']} value={platform} setter={setPlatform} />
            <OptRow title="Time" list={['week', 'month', '3m']} value={timeframe} setter={setTimeframe} />

            <button className="dc-btn full" onClick={() => setModal(false)}>
              Done
            </button>
          </div>
        </div>
      )}

      {styles}
    </div>
  );
}

/* ───── tiny sub-component ───── */
function OptRow({ title, list, value, setter }) {
  return (
    <div className="opt-row">
      <span className="opt-title">{title}</span>
      <div className="opt-scroll">
        {list.map(v => (
          <button
            key={v}
            onClick={() => setter(v)}
            className={v === value ? 'chip chip-selected' : 'chip'}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ───── themed styles (same palette as other pages) ───── */
const styles = (
  <style>{`
  :root{
    --brand-top:#fe24ad;
    --brand-bot:#a410e8;
    --brand-grad:linear-gradient(135deg,var(--brand-top),var(--brand-bot));
    --brand-top-20:rgba(254,36,173,.2);
    --brand-bot-40:rgba(164,16,232,.4);
  }
  *{box-sizing:border-box;}
  html,body{width:100%;overflow-x:hidden;background:#000;color:#fff;}

  .analytics-wrap{
    padding:1rem;
    min-height:calc(100vh - 120px);
    display:flex;
    align-items:flex-start;
    justify-content:center;
    background:
      radial-gradient(700px 500px at 20% 15%, var(--brand-top-20), transparent 60%),
      radial-gradient(600px 400px at 80% 85%, var(--brand-bot-40), transparent 60%),
      #000;
  }

  .dc-card{
    width:100%;
    background:#050008;
    border:1px solid rgba(255,255,255,.12);
    border-radius:14px;
    box-shadow:0 10px 30px rgba(0,0,0,.45);
    padding:14px 14px 10px;
    font-family:sans-serif;
  }
  .dc-note{ color:rgba(255,255,255,.85); padding:1rem; text-align:center; }

  .ana-header{
    display:flex; align-items:center; justify-content:space-between; gap:.75rem; margin-bottom:.5rem;
  }
  .ana-title{
    font-weight:900;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }

  .ana-chart{ width:100%; overflow-x:hidden; display:flex; justify-content:center; margin:10px 0; }
  .ana-xaxis{ display:flex; justify-content:space-between; padding:0 14px; margin-top:6px; }
  .ana-x{ color:#fff; font-size:12px; }

  .dc-btn{
    padding:.55rem .9rem;
    border:none;
    border-radius:10px;
    font-weight:800;
    cursor:pointer;
    background:var(--brand-grad);
    color:#fff;
    transition:transform .15s ease, box-shadow .15s ease, opacity .15s ease;
    box-shadow:0 4px 12px rgba(164,16,232,.25);
    display:inline-flex; align-items:center; justify-content:center;
  }
  .dc-btn:hover{ transform:translateY(-1px); box-shadow:0 6px 16px rgba(164,16,232,.35); }
  .dc-btn:active{ transform:translateY(0); }
  .dc-btn.ghost{
    background:transparent;
    color:#fff;
    border:1px solid rgba(255,255,255,.25);
    box-shadow:none;
  }
  .dc-btn.small{ padding:.45rem .7rem; font-size:.9rem; }
  .dc-btn.full{ width:100%; }

  .opt-row{ margin:14px 0; }
  .opt-title{
    display:block; margin-bottom:6px; font-weight:800;
    background:var(--brand-grad); -webkit-background-clip:text; -webkit-text-fill-color:transparent;
  }
  .opt-scroll{ display:flex; gap:.5rem; overflow-x:auto; }

  .chip{
    background:#0b0611;
    color:#fff;
    border:1px solid rgba(255,255,255,.25);
    border-radius:999px;
    padding:.4rem .8rem;
    font-weight:700;
    cursor:pointer;
    white-space:nowrap;
  }
  .chip-selected{
    background:rgba(254,36,173,.15);
    border-color:var(--brand-top);
  }

  .modal-overlay{
    position:fixed; inset:0; background:rgba(0,0,0,.65);
    display:flex; align-items:center; justify-content:center; z-index:9999;
  }
  .modal{
    width:92%; max-width:460px; background:#09020f;
    border:1px solid var(--brand-bot-40); border-radius:14px;
    padding:16px 16px 14px; box-shadow:0 10px 30px rgba(0,0,0,.45);
  }
  .modal-title{
    font-size:1.05rem; font-weight:800; margin-bottom:.6rem;
    background:var(--brand-grad); -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    text-align:center;
  }

  @media (max-width: 520px){
    .ana-title{ font-size:1rem; }
    .dc-btn.small{ padding:.4rem .6rem; }
  }
  `}</style>
);
